import { useEffect, useRef, useState } from "react";
import { STATUS_LINE_PROMPT } from "../shared/prompts";
import { api, type ClaudeFile } from "./api";

const when = (iso: string) => new Date(iso).toLocaleString();
const size = (n: number) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);

/** Where a file goes under ~/.claude/; the ones with a name ask for it. */
const KINDS = [
  { id: "memory", label: "CLAUDE.md", path: () => "CLAUDE.md", help: "Your instructions for every project (user memory)." },
  { id: "agent", label: "Agent", path: (n: string) => `agents/${n}.md`, named: "reviewer", help: "A subagent: agents/<name>.md." },
  { id: "command", label: "Command", path: (n: string) => `commands/${n}.md`, named: "ship", help: "A slash command: commands/<name>.md." },
  { id: "skill", label: "Skill", path: (n: string) => `skills/${n}/SKILL.md`, named: "deploy", help: "A skill: skills/<name>/SKILL.md (its other files under Other path)." },
  { id: "other", label: "Other path", path: (n: string) => n, named: "skills/deploy/scripts/run.sh", help: "Any file under ~/.claude/ the site allows: a skill's other files, hooks/, scripts/, output-styles. Scripts are made executable and the tools they call installed." },
];

function kindOf(path: string) {
  const at = (id: string) => KINDS.findIndex((k) => k.id === id);
  if (path === "CLAUDE.md") return { i: at("memory"), name: "" };
  let m = path.match(/^agents\/(.+)\.md$/); if (m) return { i: at("agent"), name: m[1] };
  m = path.match(/^commands\/(.+)\.md$/); if (m) return { i: at("command"), name: m[1] };
  m = path.match(/^skills\/([^/]+)\/SKILL\.md$/); if (m) return { i: at("skill"), name: m[1] };
  return { i: at("other"), name: path };
}


const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

function Prompts({ files, reload }: { files: ClaudeFile[]; reload: () => void }) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const prompts = files.filter((f) => f.path.startsWith("prompts/"));
  const nameOf = (path: string) => path.slice("prompts/".length, -".md".length);
  const path = `prompts/${slug(name)}.md`;

  async function edit(f: ClaudeFile) {
    setError(""); setSaved(false);
    try {
      setText(await api<string>(`/api/claude/file?path=${encodeURIComponent(f.path)}`));
      setName(nameOf(f.path)); setEditing(f.path);
      document.getElementById("prompt-editor")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) { setError((err as Error).message); }
  }
  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true); setSaved(false);
    try {
      await api("/api/claude/files", { method: "PUT", body: JSON.stringify({ path, content: text }) });
      // a renamed prompt: the old name goes
      if (editing && editing !== path) await api(`/api/claude/files?path=${encodeURIComponent(editing)}`, { method: "DELETE" });
      setEditing(path); setSaved(true); reload();
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }
  async function remove(f: ClaudeFile) {
    if (!confirm(`Delete the prompt "${nameOf(f.path)}"?`)) return;
    await api(`/api/claude/files?path=${encodeURIComponent(f.path)}`, { method: "DELETE" });
    if (editing === f.path) { setEditing(null); setName(""); setText(""); }
    reload();
  }
  const fresh = () => { setEditing(null); setName(""); setText(""); setSaved(false); };

  return (
    <>
      <p className="muted">
        What Claude Code should set up on each machine, in your own words: it does it the way that machine needs
        (macOS, Debian, Alpine and Omarchy differ), and installs what it takes. On a machine where the installer has
        run once, open Claude Code and type <code>/mylinux</code>, or <code>/mylinux status line</code> for one prompt.
      </p>
      {prompts.length > 0 && (
        <div className="list" style={{ marginBottom: 14 }}>
          {prompts.map((f) => (
            <div className="panel" key={f.id}>
              <div className="grow">
                <div className="name">{nameOf(f.path).replace(/-/g, " ")}</div>
                <div className="meta">{size(f.size)} · saved {when(f.updated_at)}</div>
              </div>
              <div className="row actions">
                <button className="btn small" onClick={() => edit(f)}>Edit</button>
                <button className="btn small danger" onClick={() => remove(f)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <form id="prompt-editor" className="panel card keyform" onSubmit={save}>
        <div className="row">
          <input type="text" className="grow" placeholder="Name, e.g. status line" value={name} required onChange={(e) => { setName(e.target.value); setSaved(false); }} aria-label="Prompt name" />
          {!editing && !prompts.some((f) => f.path === "prompts/status-line.md") && (
            <button type="button" className="btn small" onClick={() => { setName("status line"); setText(STATUS_LINE_PROMPT); setSaved(false); }}>Status line example</button>
          )}
        </div>
        <textarea rows={10} value={text} placeholder="Tell Claude Code what to set up, as you would in a chat." onChange={(e) => { setText(e.target.value); setSaved(false); }} aria-label="Prompt" />
        <div className="row">
          <button className="btn primary" disabled={busy || !text.trim() || !slug(name)}>{prompts.some((f) => f.path === path) ? "Save prompt" : "Add prompt"}</button>
          {editing && <button type="button" className="btn" onClick={fresh}>New prompt</button>}
          {saved && <span className="muted small">Saved. /mylinux on a machine uses it from now on.</span>}
        </div>
      </form>
      {error && <div className="notice error" style={{ marginBottom: 16 }}>{error}</div>}
    </>
  );
}

export function ClaudeTab() {
  const [list, setList] = useState<ClaudeFile[] | null>(null);
  const [error, setError] = useState("");
  const [kind, setKind] = useState(0);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  const load = () => api<ClaudeFile[]>("/api/claude/files").then(setList).catch((e) => { setList([]); setError(e.message); });
  useEffect(() => { load(); }, []);

  const k = KINDS[kind];
  const path = k.path(name.trim());
  const jsonProblem = (() => {
    if (!path.endsWith(".json") || !content.trim()) return "";
    try { const v = JSON.parse(content); return v && typeof v === "object" && !Array.isArray(v) ? "" : "must be a JSON object"; }
    catch (e) { return (e as Error).message; }
  })();

  function choose(i: number) { setKind(i); setName(""); setEditing(null); setContent(""); setSaved(""); }

  async function edit(f: ClaudeFile) {
    setError(""); setSaved("");
    try {
      const text = await api<string>(`/api/claude/file?path=${encodeURIComponent(f.path)}`);
      const { i, name: n } = kindOf(f.path);
      setKind(i); setName(n); setContent(text); setEditing(f.path);
      document.getElementById("claude-editor")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) { setError((err as Error).message); }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setBusy(true); setSaved("");
    try {
      await api("/api/claude/files", { method: "PUT", body: JSON.stringify({ path, content }) });
      setSaved(path); setEditing(path);
      await load();
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }

  async function remove(f: ClaudeFile) {
    if (!confirm(`Delete ${f.path} here? Machines keep their copy until you change it there.`)) return;
    await api(`/api/claude/files?path=${encodeURIComponent(f.path)}`, { method: "DELETE" });
    if (editing === f.path) choose(kind);
    load();
  }

  async function opened(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setContent(await file.text());
    // a file's own name picks the kind when it is one of the plain ones
    const at = (id: string) => KINDS.findIndex((k) => k.id === id);
    if (file.name === "CLAUDE.md") setKind(at("memory"));
    else if (file.name === "SKILL.md" && KINDS[kind].id !== "skill") setKind(at("skill"));
  }

  const files = (list ?? []).filter((f) => !f.path.startsWith("prompts/"));
  return (
    <>
      <h2 className="section" style={{ marginTop: 0 }}>Prompts</h2>
      <Prompts files={list ?? []} reload={load} />
      <details className="more" open={files.length > 0}>
      <summary>More: files for <code>~/.claude/</code> (CLAUDE.md, agents, commands, skills)</summary>
      <p className="muted">
        Files for <code>~/.claude/</code> on your machines, as they are (a prompt above is usually better: it fits each machine). <code>curl -fsSL {location.origin}/install/claude | sh</code> writes
        them there before it starts Claude Code; run it again after a change. A file the machine had already, and that
        differs, is kept once as <code>&lt;name&gt;.before-mylinux</code>. Kept encrypted, like your saved keys.
      </p>
      {list === null ? <div className="empty">Loading…</div> : files.length > 0 && (
        <div className="list" style={{ marginBottom: 14 }}>
          {files.map((f) => (
            <div className="panel" key={f.id}>
              <div className="grow">
                <div className="name mono">{f.path}</div>
                <div className="meta">{size(f.size)} · saved {when(f.updated_at)}</div>
              </div>
              <div className="row actions">
                <button className="btn small" onClick={() => edit(f)}>Edit</button>
                <button className="btn small danger" onClick={() => remove(f)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <form id="claude-editor" className="panel card keyform" onSubmit={save}>
        <div className="row">
          <select value={kind} onChange={(e) => choose(Number(e.target.value))} aria-label="Kind of file">
            {KINDS.map((x, i) => <option key={x.id} value={i}>{x.label}</option>)}
          </select>
          {k.named && (
            <input type="text" className="mono grow" placeholder={k.named} value={name} required
              onChange={(e) => setName(e.target.value)} aria-label="Name" />
          )}
          <button type="button" className="btn small" onClick={() => fileInput.current?.click()}>Open file…</button>
          <input ref={fileInput} type="file" hidden onChange={opened} />
        </div>
        <p className="muted small">{k.help} Goes to <code>~/.claude/{path || "…"}</code>.</p>
        <textarea className="mono" rows={14} value={content} spellCheck={false} placeholder="Paste the file here, or Open file…"
          onChange={(e) => { setContent(e.target.value); setSaved(""); }} aria-label="Content" />
        {jsonProblem && <div className="notice error">Not valid JSON yet: {jsonProblem}</div>}
        <div className="row">
          <button className="btn primary" disabled={busy || !content.trim() || !!jsonProblem || (!!k.named && !name.trim())}>
            {list?.some((f) => f.path === path) ? `Save ${path}` : `Add ${path || "file"}`}
          </button>
          {editing && <button type="button" className="btn" onClick={() => choose(kind)}>New file</button>}
          {saved && <span className="muted small">Saved. Machines get it the next time they run the installer.</span>}
        </div>
      </form>
      {error && <div className="notice error" style={{ marginBottom: 16 }}>{error}</div>}
      </details>
    </>
  );
}
